import React, { useEffect } from "react";
import { ScanLine, Scale, FileAudio, ArrowRight, Sparkles, Lock, ShieldCheck, UserPlus } from "lucide-react";

const MODULES = [
  { id: "yunusbol", name: "YunusBol", tag: "Reads your mail aloud, in your language", icon: ScanLine, accent: "#FFD600", live: true },
  { id: "avlegal", name: "AVLegal", tag: "AI legal case-manager & risk radar", icon: Scale, accent: "#7CC4FF", live: true },
  { id: "urmeetai", name: "urMEETai", tag: "Meeting & call intelligence, auto-minuted", icon: FileAudio, accent: "#A6F0C6", live: true },
];

export default function Landing({ onEnter, onRegister }) {
  useEffect(() => { document.documentElement.dir = "ltr"; }, []);
  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0E0F0D] text-white" data-testid="landing-page">
      {/* ambient glows */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full" style={{ background: "radial-gradient(circle, rgba(255,214,0,0.20), transparent 60%)" }} />
      <div className="pointer-events-none absolute top-1/3 -right-40 w-[480px] h-[480px] rounded-full" style={{ background: "radial-gradient(circle, rgba(124,196,255,0.16), transparent 60%)" }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "22px 22px" }} />

      <div className="relative max-w-5xl mx-auto px-6 py-8">
        {/* Nav */}
        <header className="flex items-center justify-between yb-fade-up">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-[#FFD600] text-[#0E0F0D] flex items-center justify-center font-black text-2xl" style={{ boxShadow: "0 0 0 3px #0E0F0D, 0 0 30px rgba(255,214,0,0.5)" }}>A</div>
            <div>
              <p className="yb-heading text-xl font-extrabold leading-none tracking-tight">A V Nexus</p>
              <p className="text-[11px] text-white/50 tracking-wide">powered by A V Tech</p>
            </div>
          </div>
          <button onClick={onRegister} data-testid="landing-register" className="text-sm font-bold text-white/80 hover:text-white border border-white/20 rounded-full px-4 py-2 transition-colors">
            New account
          </button>
        </header>

        {/* Hero */}
        <section className="mt-16 sm:mt-24 max-w-3xl yb-fade-up" style={{ animationDelay: "0.08s" }}>
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#FFD600] mb-5">
            <Sparkles className="w-4 h-4" /> Beyond every language barrier
          </div>
          <h1 className="yb-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tight">
            Understand everything.<br /><span className="text-[#FFD600]">In your language.</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-white/70 max-w-xl leading-relaxed">
            A V Nexus reads your mail, manages your legal matters, and turns every meeting into clear minutes — in your language, with a voice you trust.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={onEnter} data-testid="landing-enter"
              className="group inline-flex items-center gap-3 bg-[#FFD600] text-[#0E0F0D] font-extrabold text-lg rounded-full pl-7 pr-5 py-4 transition-transform active:scale-95 hover:shadow-[0_0_40px_rgba(255,214,0,0.55)]">
              Enter with your PIN
              <span className="w-9 h-9 rounded-full bg-[#0E0F0D] text-[#FFD600] flex items-center justify-center transition-transform group-hover:translate-x-1">
                <ArrowRight className="w-5 h-5" />
              </span>
            </button>
            <button onClick={onRegister} data-testid="landing-register-cta"
              className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-[#FFD600] hover:text-[#FFD600] text-white font-extrabold text-lg rounded-full px-7 py-4 transition-colors">
              <UserPlus className="w-5 h-5" /> Create your account
            </button>
          </div>
          <p className="mt-4 text-sm text-white/55 flex items-center gap-2">
            <Lock className="w-4 h-4" /> Private &amp; PIN-protected · New here?
            <button onClick={onRegister} data-testid="landing-register-inline" className="text-[#FFD600] font-bold underline underline-offset-2 hover:text-white transition-colors">
              Sign up free in a minute
            </button>
          </p>
        </section>

        {/* Modules */}
        <section className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {MODULES.map((m, i) => (
            <div key={m.id} data-testid={`landing-module-${m.id}`}
              className="yb-fade-up rounded-3xl p-5 bg-white/[0.04] border border-white/10 backdrop-blur-md hover:bg-white/[0.07] transition-colors"
              style={{ animationDelay: `${0.15 + i * 0.08}s` }}>
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: m.accent, color: "#0E0F0D" }}>
                <m.icon className="w-6 h-6" />
              </div>
              <h3 className="yb-heading text-lg font-extrabold">{m.name}</h3>
              <p className="text-sm text-white/60 mt-1 leading-snug">{m.tag}</p>
            </div>
          ))}
        </section>

        {/* Access note */}
        <section className="mt-8 rounded-3xl p-5 bg-gradient-to-r from-[#FFD600]/10 to-transparent border border-[#FFD600]/25 flex items-start gap-4 yb-fade-up" style={{ animationDelay: "0.45s" }} data-testid="landing-access-note">
          <ShieldCheck className="w-7 h-7 text-[#FFD600] shrink-0 mt-0.5" />
          <div>
            <p className="font-bold">Access is granted per feature.</p>
            <p className="text-sm text-white/60">Most people get <strong className="text-white/90">YunusBol</strong>. For the full suite — AVLegal, urMEETai and more — <strong className="text-[#FFD600]">contact SP</strong>.</p>
          </div>
        </section>

        <footer className="mt-14 pb-10 text-center text-xs text-white/40">
          © {new Date().getFullYear()} A V Tech · avtechai.org · Powered by A V Tech · More modules coming soon
        </footer>
      </div>
    </div>
  );
}
